/**
 * ListeningGoalPicker — bottom-sheet goal picker opened by tapping the feed
 * listening bar. Web port of the goal-picker Modal in iOS FeedHeaderProgress
 * (FeedHeaderProgress.tsx:154-199): dimmed overlay, white rounded-top card
 * with a centered "Daily Listening Goal" title, the settings
 * ListeningGoalSelector (its own label + description + −/+ stepper around the
 * current goal card), and a blue Done button. iOS renders the title twice —
 * once as the modal header and once inside the selector — so we do too.
 */
import { useMemo } from "react";
import { colors } from "@/ui";
import { useUserSettings } from "@/hooks/useUserSettings";
import Stepper from "@/components/Profile/settings/Stepper";
import { DEFAULT_GOAL_MINUTES } from "./useFeedStreak";

// iOS ListeningGoalSelector GOAL_OPTIONS
// (client-native/src/features/settings/components/ListeningGoalSelector.tsx).
const GOAL_OPTIONS = [
  { minutes: 5, displayName: "Light", description: "5 min/day" },
  { minutes: 10, displayName: "Moderate", description: "10 min/day" },
  { minutes: 15, displayName: "Committed", description: "15 min/day" },
  { minutes: 20, displayName: "Intense", description: "20 min/day" },
  { minutes: 25, displayName: "Hardcore", description: "25 min/day" },
];

interface ListeningGoalPickerProps {
  onGoalChange: (minutes: number) => void;
  onClose: () => void;
}

export function ListeningGoalPicker({
  onGoalChange,
  onClose,
}: ListeningGoalPickerProps) {
  const { settings } = useUserSettings();
  const currentGoal = settings?.dailyListeningGoalMinutes || DEFAULT_GOAL_MINUTES;

  const currentIndex = useMemo(() => {
    const i = GOAL_OPTIONS.findIndex((o) => o.minutes === currentGoal);
    return i >= 0 ? i : 0;
  }, [currentGoal]);
  const currentOption = GOAL_OPTIONS[currentIndex];
  const canDecrease = currentIndex > 0;
  const canIncrease = currentIndex < GOAL_OPTIONS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-[440px] rounded-t-[20px] bg-white px-5 pt-6 pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <p
          className="text-center text-lg font-bold mb-4"
          style={{ color: colors.primary }}
        >
          Daily Listening Goal
        </p>

        <div className="mb-3">
          <p className="text-base font-bold" style={{ color: colors.primary }}>
            Daily Listening Goal
          </p>
          <p
            className="text-sm mt-1 mb-2"
            style={{ color: colors.secondary }}
          >
            How many minutes of listening per day to hit your streak goal.
          </p>
          <Stepper
            canDecrease={canDecrease}
            canIncrease={canIncrease}
            onDecrease={() => onGoalChange(GOAL_OPTIONS[currentIndex - 1].minutes)}
            onIncrease={() => onGoalChange(GOAL_OPTIONS[currentIndex + 1].minutes)}
          >
            <div
              className="flex flex-col items-center justify-center rounded-xl p-2"
              style={{
                backgroundColor: colors.white,
                border: `1px solid ${colors.greyLight}`,
              }}
            >
              <span
                className="text-base font-bold"
                style={{ color: colors.primary }}
              >
                {currentOption.displayName}
              </span>
              <span className="text-sm" style={{ color: colors.secondary }}>
                {currentOption.description}
              </span>
            </div>
          </Stepper>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-xl py-3.5 mt-2 text-base font-bold"
          style={{ backgroundColor: colors.blue, color: colors.white }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
